import React, { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { FormDrawer, Badge } from '../../../components/ui';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import useUserForm from '../hooks/useUserForm';
import UserForm from './UserForm';
import { isSuperAdmin, isSameUser } from '../utils/userAccessUtils';

const CREATE_SUBTITLE = 'Create a new user and assign an organisation profile.';

const EDIT_SUBTITLE = 'Update user details and profile assignment.';

const DRAWER_WIDTH = { xs: '100%', sm: 480, md: 640, lg: 720 };

export default function UserFormDrawer({
  open,
  isEditMode = false,
  onClose,
  onSuccess,
}) {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const showOrganisationField = isSuperAdmin(loggedInUser);

  const formState = useUserForm({
    open,
    isEditMode,
    onClose,
    onSuccess,
  });

  const {
    formData,
    errors,
    isSubmitting,
    isDirty,
    validationAttempt,
    profileResponse,
    profileIsLoading,
    canLoadMoreProfiles,
    loadMoreProfile,
    handleSelectOrganisation,
    updateField,
    setFormData,
    handleClose,
    submit,
  } = formState;

  const editBadge = useMemo(() => {
    if (!isEditMode) {
      return null;
    }
    const displayName =
      [formData?.first_name, formData?.last_name].filter(Boolean).join(' ') ||
      formData?.username ||
      'User';
    return (
      <Badge variant="outline" size="sm" sx={{ fontWeight: 500 }}>
        {`Editing ${displayName}`}
      </Badge>
    );
  }, [formData?.first_name, formData?.last_name, formData?.username, isEditMode]);

  const onDrawerClose = useCallback(() => {
    if (!isSubmitting) {
      handleClose();
    }
  }, [handleClose, isSubmitting]);

  const onDrawerSubmit = useCallback(async () => {
    await submit();
  }, [submit]);

  const profileLocked = Boolean(isEditMode && isSameUser(loggedInUser, formData));

  const handleProfileChange = useCallback(
    (profile) => {
      if (isEditMode && isSameUser(loggedInUser, formData)) {
        return;
      }
      setFormData((prev) => ({ ...prev, profile }));
    },
    [formData, isEditMode, loggedInUser, setFormData]
  );

  const profileLoadMore = canLoadMoreProfiles ? loadMoreProfile : undefined;

  return (
    <FormDrawer
      open={open}
      onClose={onDrawerClose}
      title={isEditMode ? 'Edit User' : 'Create User'}
      subtitle={isEditMode ? EDIT_SUBTITLE : CREATE_SUBTITLE}
      headerBadge={editBadge}
      submitLabel={
        isSubmitting
          ? isEditMode
            ? 'Saving...'
            : 'Creating...'
          : isEditMode
            ? 'Save Changes'
            : 'Create User'
      }
      onSubmit={onDrawerSubmit}
      isSubmitting={isSubmitting}
      disableClose={isSubmitting}
      confirmBeforeClose={isDirty}
      width={DRAWER_WIDTH}
    >
      <UserForm
        formData={formData}
        errors={errors}
        updateField={updateField}
        validationAttempt={validationAttempt}
        profileOptions={profileResponse?.list || []}
        profileLoading={profileIsLoading}
        onLoadMoreProfile={profileLoadMore}
        onSelectOrganisation={handleSelectOrganisation}
        onProfileChange={handleProfileChange}
        showOrganisationField={showOrganisationField}
        disableProfileChange={profileLocked}
        profileHelperText={
          profileLocked
            ? 'You cannot change your own assigned profile.'
            : undefined
        }
      />
    </FormDrawer>
  );
}
